import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "app/hooks.ts";
import { selectUser } from "features/auth/authSlice.ts";
import {
  clearPopupSnoozesForUser,
  createPopupUserNamespace,
  hasPopupSnooze,
  hasPopupSuppression,
  snoozePopup,
  suppressPopup,
} from "./pageVideoPopupStorage.ts";
import { useGetActivePageVideoPopupsQuery } from "./pageVideoPopupsApiSlice.ts";

const normalizePath = (path: string) =>
  path.length > 1 ? path.replace(/\/+$/, "").toLowerCase() : path.toLowerCase();

export const routeMatchesPopup = (
  pathname: string,
  configuredRoute: string,
) => {
  const current = normalizePath(pathname);
  const target = normalizePath(configuredRoute);
  return target.endsWith("/*")
    ? current === target.slice(0, -2) || current.startsWith(target.slice(0, -1))
    : current === target;
};

export const resolveVideoSource = (videoUrl: string, autoPlay: boolean) => {
  try {
    const url = new URL(videoUrl);
    const youtubeId =
      url.hostname === "youtu.be"
        ? url.pathname.slice(1)
        : url.hostname === "www.youtube.com" || url.hostname === "youtube.com"
          ? (url.searchParams.get("v") ??
            (url.pathname.startsWith("/embed/")
              ? url.pathname.split("/").filter(Boolean).pop()
              : null))
          : null;
    if (youtubeId && /^[\w-]{11}$/.test(youtubeId)) {
      const searchParams = new URLSearchParams({ rel: "0" });
      if (autoPlay) searchParams.set("autoplay", "1");
      return {
        src: `https://www.youtube-nocookie.com/embed/${youtubeId}?${searchParams.toString()}`,
      };
    }
    return null;
  } catch {
    return null;
  }
};

const PageVideoPopupHost = () => {
  const { pathname } = useLocation();
  const user = useAppSelector(selectUser);
  const { data = [] } = useGetActivePageVideoPopupsQuery(undefined, {
    skip: !user,
  });
  const [neverShowAgain, setNeverShowAgain] = useState(false);
  const [snoozeRevision, setSnoozeRevision] = useState(0);
  const previousUserNamespace = useRef<string | null | undefined>(undefined);
  const userNamespace = useMemo(
    () =>
      user?.iss && user?.sub
        ? createPopupUserNamespace(user.iss, user.sub)
        : null,
    [user?.iss, user?.sub],
  );

  useEffect(() => {
    if (previousUserNamespace.current === null && userNamespace) {
      clearPopupSnoozesForUser(window.sessionStorage, userNamespace);
    }
    previousUserNamespace.current = userNamespace;
    setNeverShowAgain(false);
    setSnoozeRevision((revision) => revision + 1);
  }, [userNamespace]);

  const popup = useMemo(() => {
    if (!userNamespace) return undefined;
    return data.find((item) => {
      return (
        !hasPopupSuppression(
          window.localStorage,
          userNamespace,
          item.key,
          item.version,
        ) &&
        !hasPopupSnooze(
          window.sessionStorage,
          userNamespace,
          item.key,
          item.version,
        ) &&
        item.routes.some((route) => routeMatchesPopup(pathname, route))
      );
    });
  }, [data, pathname, snoozeRevision, userNamespace]);

  if (!popup || !userNamespace) return null;
  const videoSource = resolveVideoSource(popup.videoUrl, popup.autoPlay);
  if (!videoSource) return null;
  const close = () => {
    if (neverShowAgain) {
      suppressPopup(window.localStorage, userNamespace, popup.key, popup.version);
    } else {
      snoozePopup(window.sessionStorage, userNamespace, popup.key, popup.version);
    }
    setSnoozeRevision((revision) => revision + 1);
    setNeverShowAgain(false);
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={close}
      aria-labelledby="page-video-popup-title"
    >
      <DialogTitle id="page-video-popup-title">
        {popup.title || "Page guidance"}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            pt: "56.25%",
            overflow: "hidden",
          }}
        >
          <Box
            component="iframe"
            title={`${popup.title || "Page guidance"} video`}
            src={videoSource.src}
            allow={`accelerometer; encrypted-media; gyroscope; picture-in-picture${
              popup.autoPlay ? "; autoplay" : ""
            }`}
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            sx={{
              border: 0,
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            }}
          />
        </Box>
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={neverShowAgain}
              onChange={(event) => setNeverShowAgain(event.target.checked)}
            />
          }
          label="Don't show this video again"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} title="Close (Esc)" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageVideoPopupHost;
